import type { KitchenTicketView } from "@kopi/contracts";
import {
  nextTicketMove,
  PREPARATION_STATIONS,
  type PreparationStation,
  type TicketMove,
} from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { Skeleton } from "@kopi/ui/components/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@kopi/ui/components/tabs";
import { cn } from "@kopi/ui/lib/utils";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RadioIcon, UnplugIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { LoadMore } from "@/components/infinite-list";
import { Page, PageHeader } from "@/components/page-header";
import { kitchen } from "@/i18n/messages/kitchen";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, apiUrl, unwrap } from "@/lib/api";
import { CHANNEL_KEYS } from "@/lib/channels";
import { meScopesQuery } from "@/lib/queries";

const ALL = "all";
type StationFilter = PreparationStation | typeof ALL;
const KEY = ["kitchen-tickets"] as const;
/** While the live stream is down, the queue is re-read this often. */
const POLL_MS = 5_000;
/** Finished tickets shown at first: the latest few, the rest on demand. */
const DONE_PAGE = 8;

const useKitchenMessages = () => useMessages(kitchen);
type KitchenT = ReturnType<typeof useKitchenMessages>["t"];

type TicketRoute = ReturnType<(typeof api.v1)["kitchen-tickets"]>;
type MoveBody = { expectedRevision: number };

/** Each move's button on a ticket card (the moves themselves: `TICKET_MOVES`). */
const ACTIONS = {
  start: {
    label: "kitchen.start",
    variant: "default",
    post: (r: TicketRoute, b: MoveBody) => r.start.post(b),
  },
  ready: {
    label: "kitchen.ready",
    variant: "default",
    post: (r: TicketRoute, b: MoveBody) => r.ready.post(b),
  },
  acknowledge: {
    label: "kitchen.acknowledge",
    variant: "outline",
    post: (r: TicketRoute, b: MoveBody) => r.acknowledge.post(b),
  },
} as const satisfies Record<TicketMove, { label: string; variant: string; post: unknown }>;

function validateSearch(search: Record<string, unknown>): { station: StationFilter } {
  const station = search.station;
  return PREPARATION_STATIONS.includes(station as PreparationStation)
    ? { station: station as PreparationStation }
    : { station: ALL };
}

export const Route = createFileRoute("/_authed/kitchen")({
  staticData: { section: "workspace", crumb: "nav.kitchen" },
  validateSearch,
  component: KitchenPage,
});

/** One station's (or every station's) tickets, a page at a time (CODING_STANDARDS §4). */
const ticketsQuery = (outletId: string, station: StationFilter, view: "active" | "done", limit: number) =>
  infiniteQueryOptions({
    queryKey: [...KEY, outletId, { station, view }],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId }).kitchen.tickets.get({
          query: {
            limit,
            view,
            ...(pageParam ? { cursor: pageParam } : {}),
            ...(station === ALL ? {} : { station }),
          },
        }),
      ),
  });

/**
 * The queue updates live (PRD §20 SSE): every `tickets` event reloads the lists. If the stream drops,
 * EventSource reconnects by itself and the lists poll meanwhile.
 */
function useLiveTickets(outletId: string, station: StationFilter) {
  const queryClient = useQueryClient();
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (typeof EventSource === "undefined") return;
    const query = station === ALL ? "" : `?station=${station}`;
    const source = new EventSource(apiUrl(`/v1/outlets/${outletId}/kitchen/events${query}`), {
      withCredentials: true,
    });
    source.addEventListener("tickets", () => {
      setLive(true);
      void queryClient.invalidateQueries({ queryKey: [...KEY, outletId] });
    });
    source.onerror = () => setLive(false);
    return () => source.close();
  }, [outletId, station, queryClient]);
  return live;
}

function useNow(intervalMs: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** Who to call: the table, else the customer, else the receipt number (or the order's reference). */
function callName(t: KitchenT, ticket: KitchenTicketView) {
  if (ticket.tableLabel) return t("kitchen.table", { table: ticket.tableLabel });
  if (ticket.customerName) return ticket.customerName;
  return t("kitchen.number", { number: ticket.receiptNumber ?? ticket.reference });
}

function KitchenPage() {
  const { t } = useKitchenMessages();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { outlet } = useCurrentOutlet();
  const { station } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const outletId = outlet?.id ?? "";
  const works = Boolean(outlet) && canAt(scopes, "kitchen.queue", outletId);

  if (!works) {
    return (
      <Page>
        <PageHeader title={t("nav.kitchen")} subtitle={t("kitchen.noOutlet")} />
      </Page>
    );
  }
  return (
    <Page>
      <PageHeader title={t("nav.kitchen")} subtitle={t("kitchen.subtitle")} />
      {/* The station filter sits under the heading like every other page's tabs (Stok, Katalog). */}
      <Tabs
        value={station}
        onValueChange={(value) =>
          void navigate({
            search: { station: validateSearch({ station: value }).station },
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("kitchen.station")}>
          {[ALL, ...PREPARATION_STATIONS].map((value) => (
            <TabsTrigger key={value} value={value} className="sm:px-4">
              {t(`kitchen.station.${value as StationFilter}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Queue
        key={`${outletId}:${station}`}
        outletId={outletId}
        station={station}
        timeZone={outlet?.timezone}
      />
    </Page>
  );
}

function Queue(props: { outletId: string; station: StationFilter; timeZone: string | undefined }) {
  const { t, format } = useKitchenMessages();
  const live = useLiveTickets(props.outletId, props.station);
  const poll = live ? false : POLL_MS;
  const active = useInfiniteQuery({
    ...ticketsQuery(props.outletId, props.station, "active", 50),
    refetchInterval: poll,
  });
  const done = useInfiniteQuery({
    ...ticketsQuery(props.outletId, props.station, "done", DONE_PAGE),
    refetchInterval: poll,
  });
  const now = useNow(30_000);
  const queued = active.data?.pages.flatMap((p) => p.items) ?? [];
  const finished = done.data?.pages.flatMap((p) => p.items) ?? [];
  // The whole queue, counted by the server: the loaded pages may hold only its oldest tickets.
  const queueSize = active.data?.pages[0]?.total ?? queued.length;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-lg">
          {t("kitchen.queue")}
          {active.data && (
            <span className="ml-2 font-normal text-muted-foreground text-sm" data-testid="kitchen-count">
              {t("kitchen.queueCount", { count: queueSize })}
            </span>
          )}
        </h2>
        <p
          className={cn("flex items-center gap-1.5 text-sm", live ? "text-success" : "text-muted-foreground")}
          role="status"
          data-testid="kitchen-live"
        >
          {live ? (
            <RadioIcon className="size-4" aria-hidden />
          ) : (
            <UnplugIcon className="size-4" aria-hidden />
          )}
          {live ? t("kitchen.live") : t("kitchen.reconnecting")}
        </p>
      </div>
      {active.isPending ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      ) : active.isError ? (
        <div className="flex flex-col items-start gap-2" role="alert">
          <p className="text-destructive text-sm">{t("kitchen.loadFailed")}</p>
          <Button type="button" variant="outline" onClick={() => void active.refetch()}>
            {t("kitchen.retry")}
          </Button>
        </div>
      ) : queued.length === 0 ? (
        <p className="rounded-xl border border-dashed p-8 text-center text-muted-foreground text-sm">
          {t("kitchen.empty")}
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label={t("kitchen.queue")}>
          {queued.map((ticket) => (
            <li key={ticket.id}>
              <TicketCard ticket={ticket} now={now} />
            </li>
          ))}
        </ul>
      )}
      <LoadMore pages={active} />

      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-lg">{t("kitchen.done")}</h2>
        {done.data && finished.length === 0 && (
          <p className="text-muted-foreground text-sm">{t("kitchen.doneEmpty")}</p>
        )}
        <ul className="flex flex-col divide-y rounded-xl border" aria-label={t("kitchen.done")}>
          {finished.map((ticket) => (
            <li
              key={ticket.id}
              className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
              data-testid="kitchen-done-ticket"
            >
              <span className="min-w-0">
                <span className="font-medium">{callName(t, ticket)}</span>
                <span className="text-muted-foreground">
                  {" · "}
                  {ticket.lines.map((l) => `${l.quantity}× ${l.name}`).join(", ")}
                </span>
              </span>
              <span className="flex items-center gap-2 text-muted-foreground">
                {ticket.closedBy &&
                  ticket.closedAt &&
                  t("kitchen.by", {
                    name: ticket.closedBy,
                    time: format.time(ticket.closedAt, props.timeZone),
                  })}
                <StatusBadge status={ticket.status} />
              </span>
            </li>
          ))}
        </ul>
        <LoadMore pages={done} />
      </section>
    </div>
  );
}

/**
 * A ticket's status in the app's badge vocabulary (as on Staf and Pelanggan): outline while it waits or
 * is made, success once ready, outline when a change was acknowledged. Being made adds a Forest dot; the
 * word still says it, so the colour never carries the status alone.
 */
function StatusBadge(props: { status: KitchenTicketView["status"] }) {
  const { t } = useKitchenMessages();
  if (props.status === "ready") return <Badge variant="success">{t("kitchen.status.ready")}</Badge>;
  return (
    <Badge variant="outline">
      {props.status === "in_progress" && (
        <span aria-hidden className="size-1.5 rounded-full bg-primary motion-safe:animate-pulse" />
      )}
      {t(`kitchen.status.${props.status}`)}
    </Badge>
  );
}

function TicketCard(props: { ticket: KitchenTicketView; now: number }) {
  const { ticket } = props;
  const { t } = useKitchenMessages();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const next = nextTicketMove(ticket);
  const action = next ? ACTIONS[next] : undefined;
  const move = useMutation({
    mutationFn: async (move: (typeof ACTIONS)[TicketMove]) =>
      unwrap(
        await move.post(api.v1["kitchen-tickets"]({ ticketId: ticket.id }), {
          expectedRevision: ticket.revision,
        }),
      ),
    onSuccess: () => setError(null),
    onError: (cause) => {
      const code = cause instanceof ApiRequestError ? cause.code : undefined;
      setError(
        code === "ticket_revision_conflict" || code === "ticket_transition_invalid"
          ? t("kitchen.error.changed")
          : code === "stock_already_released"
            ? t("kitchen.error.released")
            : t("kitchen.error.failed"),
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: [...KEY, ticket.outletId] }),
  });
  const minutes = Math.floor((props.now - new Date(ticket.createdAt).getTime()) / 60_000);
  const amendment = ticket.kind !== "order";
  return (
    <Card
      className={cn(
        "h-full gap-3",
        ticket.kind === "cancel" && "border-destructive",
        ticket.kind === "change" && "border-warning",
        ticket.status === "in_progress" && "border-primary",
      )}
      data-testid="kitchen-ticket"
      data-kind={ticket.kind}
      data-status={ticket.status}
    >
      <CardHeader className="gap-1">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-xl">{callName(t, ticket)}</CardTitle>
          <span className="shrink-0 text-muted-foreground text-sm tabular-nums">
            {minutes < 1 ? t("kitchen.justNow") : t("kitchen.minutes", { count: minutes })}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground text-sm">
          <span>{t(CHANNEL_KEYS[ticket.orderType])}</span>
          <span aria-hidden>·</span>
          <span>{t(`kitchen.station.${ticket.station}`)}</span>
          {amendment ? (
            <Badge variant={ticket.kind === "cancel" ? "destructive" : "warning"}>
              {t(ticket.kind === "cancel" ? "kitchen.kind.cancel" : "kitchen.kind.change")}
            </Badge>
          ) : (
            <StatusBadge status={ticket.status} />
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        {amendment && (
          <p className="font-medium text-sm">
            {ticket.kind === "cancel" ? t("kitchen.cancelHint") : t("kitchen.changeHint")}
          </p>
        )}
        {/* Not paginated (CODING_STANDARDS §4 exception): an order has at most 100 lines. */}
        <ul className="flex flex-col gap-2">
          {ticket.lines.map((line) => (
            <li key={line.orderLineId} className="flex gap-3">
              <span
                className={cn(
                  "min-w-9 font-bold text-lg tabular-nums",
                  line.quantity < 0 && "text-destructive",
                  ticket.kind === "cancel" && "line-through",
                )}
              >
                {line.quantity < 0 ? `−${-line.quantity}` : line.quantity}×
              </span>
              <span className="min-w-0">
                <span className={cn("font-semibold", ticket.kind === "cancel" && "line-through")}>
                  {line.name}
                  {/* Empty for a product sold in one variant only (set when the ticket is made, #85). */}
                  {line.variantName ? ` (${line.variantName})` : ""}
                </span>
                {line.modifiers.length > 0 && (
                  <span className="block text-muted-foreground text-sm">{line.modifiers.join(", ")}</span>
                )}
                {line.note && (
                  <span className="block font-medium text-sm">{t("kitchen.note", { note: line.note })}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
        {ticket.reason && (
          <p className="text-muted-foreground text-sm">{t("kitchen.reason", { reason: ticket.reason })}</p>
        )}
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
        {/* A ticket that left the queue has no move left. */}
        {action && (
          <Button
            type="button"
            size="lg"
            className="mt-auto w-full"
            variant={action.variant}
            disabled={move.isPending}
            onClick={() => move.mutate(action)}
          >
            {move.isPending ? t("kitchen.working") : t(action.label)}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
